/**
 * Hook to access BabylonManager instance
 */

import { useCallback, useMemo } from "react";
import type { PartialBabylonConfig } from "../../config/schemas";
import { useBabylonContext } from "../BabylonContext";
import type { UseBabylonManagerReturn } from "../types";

/**
 * Hook to access and control the BabylonManager
 * @returns Manager instance and control functions
 */
export function useBabylonManager(): UseBabylonManagerReturn {
  const context = useBabylonContext();
  const {
    manager,
    isInitialized,
    isInitializing,
    error,
    initialize: contextInitialize,
    dispose: contextDispose,
  } = context;

  const resolvedManager = useMemo(() => {
    return manager;
  }, [manager]);

  // Initialize function - use context's initialize if available
  const initialize = useCallback(
    async (config?: PartialBabylonConfig) => {
      if (contextInitialize) {
        await contextInitialize(config);
        return;
      }

      if (!resolvedManager) {
        throw new Error("BabylonManager is not available");
      }

      if (resolvedManager.isInitialized || resolvedManager.isInitializing) {
        return;
      }

      await resolvedManager.initialize(config);
    },
    [contextInitialize, resolvedManager],
  );

  // Dispose function - use context's dispose if available
  const dispose = useCallback(() => {
    if (contextDispose) {
      contextDispose();
    } else {
      console.warn("Dispose function not available in context");
    }
  }, [contextDispose]);

  return {
    dispose,
    error,
    initialize,
    isInitialized,
    isInitializing,
    manager: resolvedManager,
  };
}
